import { createContext, Fragment, useContext, useMemo } from 'react'
import { useAppSelector } from 'store/hooks'
import { Grid } from '@material-ui/core'
import {
  selectConciliationCFDIFilter,
  selectConciliationCFDISActiveSection,
} from 'lib/helpers/selectors'
import CFDIFilters from '../CFDIFilters/CFDIFilters'
import CFDIMovementsTable from '../CFDIMovementsTable/CFDIMovementsTable'
import CFDIToolbar from '../CFDIToolbar/CFDIToolbar'
import LedgerAccounts from '../LedgerAccounts/LedgerAccounts'
import SyncCFDIsDialog from 'components/Shared/SyncCFDIsDialog/SyncCFDIsDialog'
import useGetCFDIs from 'hooks/useGetCFDIs'
import useGetSelectedEmisor from 'hooks/Shared/useGetSelectedEmisor'
import useSyncCFDIs, { IUseSyncCFDIs } from 'hooks/Invoices/CFDIs/useSyncCFDIs'

type TCFDIMovementsContext = IUseSyncCFDIs

const CFDIMovementsContext = createContext<TCFDIMovementsContext | null>(null)

const sections: Record<number, () => JSX.Element> = {
  0: CFDIMovementsTable,
  1: LedgerAccounts,
  99: () => <Fragment />,
}

const CFDIMovements = () => {
  const cfdiActiveSection = useAppSelector(selectConciliationCFDISActiveSection)
  const cfdiFilters = useAppSelector(selectConciliationCFDIFilter)

  const { refetch: refetchCFDIs } = useGetCFDIs()
  const { emisor, emisoresQuery } = useGetSelectedEmisor()

  const { isCFDIsSyncOpen, toggleSyncDialog, syncMutation } = useSyncCFDIs({
    emisoresQuery,
    emisor,
    onSuccess: () => refetchCFDIs(),
  })

  const ActiveSectionElement = useMemo(
    () => sections[cfdiActiveSection] || sections[99],
    [cfdiActiveSection]
  )

  const shouldDisplayFilters = useMemo(
    () => cfdiFilters.display && cfdiActiveSection === 0,
    [cfdiFilters.display, cfdiActiveSection]
  )

  const memoizedContextValue = useMemo(
    (): TCFDIMovementsContext => ({
      isCFDIsSyncOpen,
      syncMutation,
      toggleSyncDialog,
    }),
    [isCFDIsSyncOpen, syncMutation, toggleSyncDialog]
  )

  return (
    <CFDIMovementsContext.Provider value={memoizedContextValue}>
      <Grid item xs={12} md={6}>
        <CFDIToolbar />

        <SyncCFDIsDialog
          open={isCFDIsSyncOpen}
          loading={syncMutation.isLoading}
          toggleSyncDialog={toggleSyncDialog}
          syncCFDIS={syncMutation}
        />

        {shouldDisplayFilters && <CFDIFilters />}
        <ActiveSectionElement />
      </Grid>
    </CFDIMovementsContext.Provider>
  )
}

export const useCFDIMovementsContext = () => {
  const context = useContext(CFDIMovementsContext)
  if (!context) {
    throw new Error(
      'useCFDIMovementsContext must be used within a CFDIMovementsContext'
    )
  }
  return context
}

export default CFDIMovements
